import { TTickEvent, eventStream } from './index';
import { TQuote } from 'src/types';

export type PlaybackSpeed = 1 | 2 | 5 | 10 | 20 | 50 | 100 | 1000;

export interface SchedulerOptions {
  speed: PlaybackSpeed;
  granularity?: number; // Granularity in seconds (60 for 1-minute candles)
}

export class PlaybackScheduler {
  private speed: PlaybackSpeed;
  private granularity: number; // 0 = tick mode, 60+ = candle mode
  private displayIntervalSec: number = 0; // 0 = use candle boundary gap, >0 = fixed delay in seconds between batch displays
  private lastEventEpoch: number | null = null;
  private peekedEvent: TTickEvent | null = null; // Buffer for "peeked" events in candle mode
  
  constructor(opts: SchedulerOptions){ 
    this.speed = opts.speed; 
    this.granularity = opts.granularity ?? 0;
  }
  
  setSpeed(speed: PlaybackSpeed){ this.speed = speed; }
  
  setGranularity(granularity: number): void {
    this.granularity = granularity;
    this.peekedEvent = null; // Clear peeked event when granularity changes
    console.log('[PlaybackScheduler] Granularity set to:', granularity, 'seconds');
  }
  
  getGranularity(): number {
    return this.granularity;
  }

  /**
   * Set the display interval (how often the chart updates during replay).
   * When set to a value smaller than the candle granularity, the delay between
   * batch displays uses this interval instead of the candle boundary gap.
   * For example: granularity=60 (1-min candles), displayIntervalSec=1 means
   * a new candle appears every 1 second instead of every 60 seconds.
   * Set to 0 to use default candle boundary gap timing.
   */
  setDisplayInterval(sec: number): void {
    this.displayIntervalSec = sec;
    console.log('[PlaybackScheduler] Display interval set to:', sec, 'seconds');
  }

  getDisplayInterval(): number {
    return this.displayIntervalSec;
  }
  
  /**
   * Reset the scheduler state (clear peeked events)
   */
  reset(): void {
    this.peekedEvent = null;
    this.lastEventEpoch = null;
  }
  
  getBatchSize(): number {
    switch(this.speed){
      case 1: return 1; // Process 1 tick at a time for real-time playback
      case 2: return 2;
      case 5: return 5;
      case 10: return 10;
      case 20: return 20;
      case 50: return 50;
      case 100: return 100;
      case 1000: return 1000; // Ultra-fast for testing
      default: return 1;
    }
  }
  
  getFrameDelayMs(): number {
    if (this.speed >= 100) return 0; // No delay for ultra-fast
    if (this.speed >= 20) return 4;
    if (this.speed >= 5) return 8;
    return 16;
  }
  
  /**
   * Calculate delay for replay playback.
   * 
   * For candle mode (granularity > 0): Use the **candle-boundary gap** derived
   * from the actual event epochs so that the delay is always correct even when
   * `this.granularity` is momentarily stale (e.g. after a mid-replay granularity
   * change before the scheduler has been re-initialised).
   * 
   * For tick mode (granularity = 0): Use the actual epoch gap between events,
   * falling back to 1 second when events share the same epoch.
   * 
   * The speed multiplier affects the delay:
   * - Speed 1 = real-time (full delay)
   * - Speed 2 = 2x faster (half delay)
   * - etc.
   * 
   * @param currentEventEpoch - Epoch (ms) of the last event in the current batch
   * @param nextEventEpoch    - Epoch (ms) of the first event in the next batch
   */
  getRealtimeDelayMs(currentEventEpoch: number, nextEventEpoch: number): number {
    const speedDivisor = this.speed;
    
    if (this.granularity > 0) {
      // When a display interval is set and is smaller than the candle granularity,
      // use it as the fixed delay between batch displays. This allows showing
      // one candle per displayIntervalSec (e.g., 1 candle/second with 1-min candles).
      if (this.displayIntervalSec > 0 && this.displayIntervalSec < this.granularity) {
        const baseDelayMs = this.displayIntervalSec * 1000;
        const scaledDelay = Math.floor(baseDelayMs / speedDivisor);
        console.log('[PlaybackScheduler] delay from display interval:', baseDelayMs, 'ms, scaled:', scaledDelay, 'ms');
        return Math.max(scaledDelay, 16);
      }

      // Compute the gap between the two candle boundaries the events fall into.
      // This naturally equals `granularity * 1000` for well-spaced data but is
      // self-correcting when the data interval differs from `this.granularity`.
      const currentBoundary = this.getCandleBoundary(currentEventEpoch);
      const nextBoundary    = this.getCandleBoundary(nextEventEpoch);
      const boundaryGapMs   = nextBoundary - currentBoundary;

      if (boundaryGapMs > 0) {
        const scaledDelay = Math.floor(boundaryGapMs / speedDivisor);
        console.log('[PlaybackScheduler] delay from boundary gap:', boundaryGapMs, 'ms, scaled:', scaledDelay, 'ms');
        return Math.max(scaledDelay, 16);
      }

      // Same boundary (shouldn't normally happen – nextBatch groups by boundary).
      // Fall back to the configured granularity.
      const baseDelayMs = this.granularity * 1000;
      const scaledDelay = Math.floor(baseDelayMs / speedDivisor);
      return Math.max(scaledDelay, 16);
    }
    
    // Tick mode: use the actual epoch gap between events for natural pacing
    const gapMs = nextEventEpoch - currentEventEpoch;
    if (gapMs > 0) {
      const scaledDelay = Math.floor(gapMs / speedDivisor);
      return Math.max(scaledDelay, 16);
    }

    // Fall back to 1-second delay when epochs are identical
    const baseDelayMs = 1000;
    const scaledDelay = Math.floor(baseDelayMs / speedDivisor);
    return Math.max(scaledDelay, 16);
  }
  
  setLastEventEpoch(epoch: number): void {
    this.lastEventEpoch = epoch;
  }
  
  getLastEventEpoch(): number | null {
    return this.lastEventEpoch;
  }

  /**
   * Return the epoch of the next event that will start the next batch.
   * This is the scheduler's internal peekedEvent (consumed from the iterator
   * during the previous nextBatch() call but not yet part of a returned batch).
   * Returns null if no event has been peeked.
   */
  peekNextEpoch(): number | null {
    return this.peekedEvent?.epoch ?? null;
  }
  
  /**
   * Helper to get the candle boundary epoch for a given timestamp.
   * For example, with 60s granularity, all events from 12:14:00 to 12:14:59 
   * belong to the 12:14:00 candle.
   */
  private getCandleBoundary(epoch: number): number {
    if (this.granularity <= 0) return epoch;
    const granularityMs = this.granularity * 1000;
    return Math.floor(epoch / granularityMs) * granularityMs;
  }
  
  /**
   * Get the next batch of events for playback.
   * 
   * For tick mode (granularity = 0): Returns `batchSize` events
   * For candle mode (granularity > 0): Returns all events within the same candle interval
   * 
   * This ensures that when replaying 1-minute candles, all sub-events within 
   * the same minute are grouped together and displayed as one candle update.
   */
  nextBatch(it: Generator<TTickEvent, void, unknown>): TTickEvent[] {
    const events: TTickEvent[] = [];
    
    // For candle mode, group all events in the same candle interval
    if (this.granularity > 0) {
      let currentCandleBoundary: number | null = null;
      
      // Start with any peeked event from previous batch
      if (this.peekedEvent) {
        events.push(this.peekedEvent);
        currentCandleBoundary = this.getCandleBoundary(this.peekedEvent.epoch);
        this.peekedEvent = null;
      }
      
      while (true) {
        const r = it.next();
        if (r.done) break;
        
        const event = r.value;
        const eventCandleBoundary = this.getCandleBoundary(event.epoch);
        
        // First event sets the candle boundary
        if (currentCandleBoundary === null) {
          currentCandleBoundary = eventCandleBoundary;
          events.push(event);
          continue;
        }
        
        // If this event belongs to a different candle, save it for next batch
        if (eventCandleBoundary !== currentCandleBoundary) {
          // Store this event for the next batch
          this.peekedEvent = event;
          break;
        }
        
        events.push(event);
      }
      
      return events;
    }
    
    // For tick mode, use standard batch size
    const bs = this.getBatchSize();
    for(let i=0;i<bs;i++){
      const r = it.next();
      if(r.done) break;
      events.push(r.value);
    }
    return events;
  }
  toQuotes(events: TTickEvent[]): TQuote[] { return eventStream.toQuotes(events); }
}
